import { useParams } from 'react-router-dom';

import {
  ConceptGroupDetailView,
  useConceptGroupDetail,
} from '@verantos-dev/feature-concept-group-detail';

import { useConceptGroupBreadcrumbs } from '../hooks/useConceptGroupBreadcrumbs';
import { ConceptGroupDraftReview } from './concept-group/containers/ConceptGroupDraftReview';

/**
 * Read-only concept-group detail route (``/projects/:projectId/concept-group/:id``)
 * for the agent-first project page — renders in the right pane instead of the
 * old modal. The actual detail (header + concepts grid + CSV) lives in the
 * shared {@link ConceptGroupDetailView}, which the preview page also resolves
 * to in place once a proposal is created (OR-6126).
 *
 * This route mount supplies the breadcrumb trail (OR-6123 F8) — the project
 * header no longer carries it. The preview mount omits breadcrumbs.
 *
 * TODO: cleanup with research_assistant_gen_3 ff
 */
export const ProjectConceptGroupViewPage = () => {
  const { conceptGroupId = '', projectId = '' } = useParams<{
    conceptGroupId: string;
    projectId: string;
  }>();
  // Same query key as ConceptGroupDetailView → TanStack dedupes the fetch;
  // we only read the name here to label the trailing breadcrumb crumb.
  const detailQuery = useConceptGroupDetail(conceptGroupId || null);
  const { breadcrumbs } = useConceptGroupBreadcrumbs({
    conceptGroupName: detailQuery.data?.conceptGroupName,
    isLoading: detailQuery.isLoading,
  });

  // OR-6288 (single view while Draft): the canonical route renders BOTH states
  // conditionally — the draft review surface (the preview implementation,
  // reused via the detail's draftPreviewId) while Draft, the library view once
  // Ready. Deliberately DATA-driven, not flag-gated: draftPreviewId only
  // exists on drafts the new workflow created, so its presence IS the signal
  // (and the FE then can't disagree with the backend about which world it's in).
  const draftPreviewId = detailQuery.data?.draftPreviewId;
  // NB: the API's status value is 'Draft' (the asset-status model's display
  // casing — same comparison ConceptGroupDetailView uses), not 'DRAFT'.
  if (projectId && detailQuery.data?.assetStatus === 'Draft' && draftPreviewId) {
    return <ConceptGroupDraftReview previewId={draftPreviewId} projectId={projectId} />;
  }

  return <ConceptGroupDetailView conceptGroupId={conceptGroupId} breadcrumbs={breadcrumbs} />;
};
